import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSeo } from "@/hooks/useSeo";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  useSeo("Admin Login | OyeBest", "OyeBest admin dashboard login.");
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user?.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f1115] px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <span className="font-display text-3xl font-extrabold text-white">Oye<span className="text-primary">Best</span></span>
          <p className="text-white/50 mt-1 text-sm">Admin Dashboard</p>
        </div>
        <form onSubmit={submit} className="bg-white rounded-3xl p-7 shadow-2xl space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-accent text-primary flex items-center justify-center mx-auto"><Lock className="w-5 h-5" /></div>
          <div className="space-y-1.5"><Label className="text-sm font-semibold">Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl h-12" data-testid="admin-email" /></div>
          <div className="space-y-1.5"><Label className="text-sm font-semibold">Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl h-12" data-testid="admin-password" /></div>
          {error && <p className="text-sm text-destructive" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={submitting} className="w-full rounded-full h-12 font-semibold gap-2" data-testid="admin-login-btn">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
          </Button>
        </form>
      </div>
    </div>
  );
}
